import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ensureAuthenticated, isAutoLoginEnabled } from "@/lib/auth";
import { getBackendUrl } from "@/lib/backend";
import { formatLakhs } from "@/utils/analyticsConfidence";
import {
  deriveBlockOshHistogram,
  deriveDonutGradient,
  deriveLandingFeatures,
  deriveSqaafQualityDonut,
} from "@/utils/deriveLandingPreview";
import LandingDistrictMapSection from "@/components/LandingDistrictMapSection";
import "./LandingPage.css";
import { toTitleCase } from "../utils/toTitleCase";

const API = `${getBackendUrl()}/api`;
const PUNE_DISTRICT_CODE = "2725";

const FeatureIcon = ({ name }) => {
  switch (name) {
    case "udise":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>;
    case "pgi":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z" /><path d="M9 14.5 7 22l5-3 5 3-2-7.5" /></svg>;
    case "sqaf":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l2 2 4-4" /><path d="M5 4h14v16H5z" /><path d="M9 2h6v4H9z" /></svg>;
    case "nas":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5z" /></svg>;
    case "nipun":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>;
    case "ger":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="5" x2="5" y2="19" /><circle cx="6.5" cy="6.5" r="2.5" /><circle cx="17.5" cy="17.5" r="2.5" /></svg>;
    case "pat":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l2 2 4-4" /><path d="M4 4h16v16H4z" /><path d="M8 2h8v4H8z" /><path d="M8 12h8" /></svg>;
    case "schools":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /><path d="M9 9h.01M9 13h.01M9 17h.01M14 13h.01M14 17h.01" /></svg>;
    case "students":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m22 10-10 5-10-5 10-5 10 5Z" /><path d="M6 12v5c3 2 9 2 12 0v-5" /></svg>;
    case "teachers":
      return <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4" /><path d="M6 21v-2a6 6 0 0 1 12 0v2" /><path d="M9 8h6" /></svg>;
    default:
      return null;
  }
};

const SchoolIllustration = () => (
  <svg className="school-illustration" viewBox="0 0 540 280" fill="none" aria-hidden="true">
    <path d="M25 242h490" stroke="currentColor" strokeOpacity="0.08" strokeWidth="10" strokeLinecap="round" />
    <path d="M346 112h118v122H346V112Z" fill="#f7c77d" />
    <path d="M373 68h65l48 44H326l47-44Z" fill="#e8794a" />
    <path d="M388 20v44" stroke="#2f6fed" strokeWidth="8" strokeLinecap="round" />
    <path d="M388 25h44l-8 12 8 12h-44V25Z" fill="#6da2ff" />
    <path d="M118 138h238v96H118v-96Z" fill="#ffd89a" />
    <path d="M94 104h286v40H94l60-54h166l60 54Z" fill="#ef8b55" />
    <circle cx="405" cy="137" r="24" fill="white" fillOpacity="0.88" />
    <path d="M405 124v13l10 6" stroke="#2563eb" strokeWidth="4" strokeLinecap="round" />
    <g fill="#5da0ff" fillOpacity="0.65">
      <rect x="148" y="156" width="28" height="34" rx="5" />
      <rect x="200" y="156" width="28" height="34" rx="5" />
      <rect x="252" y="156" width="28" height="34" rx="5" />
      <rect x="369" y="160" width="24" height="30" rx="5" />
      <rect x="416" y="160" width="24" height="30" rx="5" />
    </g>
    <path d="M224 234v-46h48v46" fill="#7c4a2d" />
    <circle cx="77" cy="220" r="35" fill="#86d37f" />
    <circle cx="40" cy="228" r="24" fill="#62be72" />
    <circle cx="491" cy="220" r="35" fill="#86d37f" />
    <circle cx="526" cy="229" r="22" fill="#62be72" />
    <path d="M38 110c18-20 49-20 67 0M438 77c14-16 39-16 53 0" stroke="currentColor" strokeOpacity="0.08" strokeWidth="14" strokeLinecap="round" />
  </svg>
);

const LandingPage = ({ onLogin }) => {
  const navigate = useNavigate();
  const [loadingCard, setLoadingCard] = useState(null);
  const [enterLoading, setEnterLoading] = useState(false);
  const [liveKpis, setLiveKpis] = useState(null);
  const [previewBlocks, setPreviewBlocks] = useState([]);
  const [sqaafBands, setSqaafBands] = useState(null);
  const [pgiOverview, setPgiOverview] = useState(null);
  const [gerOverview, setGerOverview] = useState(null);
  const [nipunOverview, setNipunOverview] = useState(null);
  const [patOverview, setPatOverview] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      axios.get(`${API}/state/overview`),
      axios.get(`${API}/districts/${PUNE_DISTRICT_CODE}/blocks`),
      axios.get(`${API}/sqaaf/overview`),
      axios.get(`${API}/pgi/overview`),
      axios.get(`${API}/ger/overview`),
      axios.get(`${API}/nipun/overview`),
      axios.get(`${API}/pat/overview`),
    ]).then(([overviewRes, blocksRes, sqaafRes, pgiRes, gerRes, nipunRes, patRes]) => {
      if (cancelled) return;
      if (overviewRes.status === "fulfilled" && overviewRes.value?.data) {
        setLiveKpis(overviewRes.value.data);
      } else {
        setLiveKpis(null);
      }
      if (blocksRes.status === "fulfilled" && Array.isArray(blocksRes.value?.data)) {
        setPreviewBlocks(blocksRes.value.data);
      } else {
        setPreviewBlocks([]);
      }
      const bandDistribution =
        sqaafRes.status === "fulfilled"
          ? sqaafRes.value?.data?.band_distribution ?? sqaafRes.value?.data?.band_summary
          : null;
      if (Array.isArray(bandDistribution) && bandDistribution.length) {
        setSqaafBands(bandDistribution);
      } else {
        setSqaafBands(null);
      }
      if (pgiRes.status === "fulfilled" && pgiRes.value?.data) {
        setPgiOverview(pgiRes.value.data);
      } else {
        setPgiOverview(null);
      }
      if (gerRes.status === "fulfilled" && gerRes.value?.data) {
        setGerOverview(gerRes.value.data);
      } else {
        setGerOverview(null);
      }
      if (nipunRes.status === "fulfilled" && nipunRes.value?.data) {
        setNipunOverview(nipunRes.value.data);
      } else {
        setNipunOverview(null);
      }
      if (patRes.status === "fulfilled" && patRes.value?.data) {
        setPatOverview(patRes.value.data);
      } else {
        setPatOverview(null);
      }

      const results = [overviewRes, blocksRes, sqaafRes, pgiRes, gerRes, nipunRes, patRes];
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === results.length) {
        toast.error("Unable to load live data. Ensure the dashboard backend service is running, then refresh.");
      } else if (failed > 0) {
        toast.warning(`Some dashboard previews could not be loaded (${failed} of ${results.length}).`);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const previewSchools = liveKpis?.total_schools
    ? liveKpis.total_schools.toLocaleString("en-IN")
    : null;
  const previewStudents = liveKpis?.total_students ? formatLakhs(liveKpis.total_students) : null;
  const previewDataEntry =
    typeof liveKpis?.data_entry_percentage === "number"
      ? `${liveKpis.data_entry_percentage.toFixed(1)}%`
      : null;
  const previewOsh =
    typeof liveKpis?.avg_shi === "number" ? `${liveKpis.avg_shi.toFixed(1)}%` : null;
  const previewSourceLabel = liveKpis ? "Live district data" : "Awaiting live data";
  const oshHistogram = deriveBlockOshHistogram(previewBlocks);
  const qualityDonut = deriveSqaafQualityDonut(sqaafBands || []);
  const donutStyle = deriveDonutGradient(qualityDonut);
  const features = deriveLandingFeatures(liveKpis, sqaafBands, pgiOverview, gerOverview, nipunOverview, patOverview);

  const loginAndGo = async (targetPath, successMessage) => {
    try {
      const authUser = await ensureAuthenticated();
      if (!authUser) {
        navigate("/login", { state: { from: targetPath } });
        return;
      }
      if (onLogin) onLogin(authUser);
      toast.success(successMessage || `Signed in as ${authUser.full_name}`);
      navigate(targetPath);
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed. Try signing in from the login page.";
      toast.error(msg);
    }
  };

  const handleFeatureClick = async (feature) => {
    if (!feature.path) {
      navigate("/login", { state: { from: "/executive-dashboard" } });
      return;
    }
    setLoadingCard(feature.title);
    try {
      await loginAndGo(feature.path, `Opening ${feature.title}…`);
    } finally {
      setLoadingCard(null);
    }
  };

  const handleEnterDashboard = async () => {
    if (!isAutoLoginEnabled()) {
      navigate("/login", { state: { from: "/executive-dashboard" } });
      return;
    }
    setEnterLoading(true);
    try {
      await loginAndGo("/executive-dashboard", "Opening Executive Dashboard…");
    } finally {
      setEnterLoading(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="landing-page" data-theme="blue">
      <div className="page-shell">
        <header className="navbar">
          <div className="container nav-content">
            <button type="button" className="brand" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="School Education Analytics Portal">
              <span className="brand-icon" aria-hidden="true">
                <svg className="large-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 10L12 5 2 10l10 5 10-5Z" />
                  <path d="M6 12v5c3 2 9 2 12 0v-5" />
                  <path d="M22 10v6" />
                </svg>
              </span>
              <span className="brand-text">
                <strong>School Education</strong>
                <span>Analytics Portal</span>
              </span>
            </button>

            <nav className="nav-links" aria-label="Primary navigation">
              <a href="#dashboards" onClick={(e) => { e.preventDefault(); scrollToSection("dashboards"); }}>Dashboards</a>
              <a href="#impact" onClick={(e) => { e.preventDefault(); scrollToSection("impact"); }}>Reports</a>
              <a href="#dashboards" onClick={(e) => { e.preventDefault(); scrollToSection("dashboards"); }}>Insights</a>
              <a href="#impact" onClick={(e) => { e.preventDefault(); scrollToSection("impact"); }}>Resources</a>
              <a href="#impact" onClick={(e) => { e.preventDefault(); scrollToSection("impact"); }}>About</a>
            </nav>

            <div className="nav-actions">
              <span className="location-pill">
                <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
                Pune District · Maharashtra
              </span>
              <button type="button" className="login-btn" onClick={() => navigate("/login")}>
                Sign In →
              </button>
            </div>
          </div>
        </header>

        <main>
          <section className="hero">
            <div className="container hero-grid">
              <div className="hero-content">
                <div className="eyebrow">
                  <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 3v18h18" />
                    <path d="m19 9-5 5-4-4-3 3" />
                  </svg>
                  Evidence-based. Data-driven. Student-focused.
                </div>
                <h1>
                  Smarter Decisions.
                  <br />
                  <span className="text-gradient">Stronger Schools.</span>
                </h1>
                <p className="hero-copy">
                  An integrated analytics platform to monitor school health, enrolment, infrastructure, learning outcomes and compliance across every block and school.
                </p>
                <div className="hero-actions">
                  <button type="button" className="btn btn-primary" onClick={handleEnterDashboard} disabled={enterLoading}>
                    {enterLoading ? "Signing in…" : "Enter Dashboard →"}
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => scrollToSection("dashboards")}>
                    Explore Features ↓
                  </button>
                </div>

                <div className="trust-row" aria-label="Key benefits">
                  <div className="trust-card">
                    <span className="icon-box" style={{ width: 42, height: 42, margin: 0 }}>
                      <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" /><path d="m9 12 2 2 4-4" /></svg>
                    </span>
                    <div><b>Trusted Data</b><span className="trust-sub">Accurate &amp; reliable</span></div>
                  </div>
                  <div className="trust-card">
                    <span className="icon-box" style={{ width: 42, height: 42, margin: 0 }}>
                      <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                    </span>
                    <div><b>Real-time Insights</b><span className="trust-sub">Always up to date</span></div>
                  </div>
                  <div className="trust-card">
                    <span className="icon-box" style={{ width: 42, height: 42, margin: 0 }}>
                      <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    </span>
                    <div><b>Better Decisions</b><span className="trust-sub">For every student</span></div>
                  </div>
                </div>
              </div>

              <div className="hero-visual" aria-label="Analytics dashboard preview">
                <SchoolIllustration />

                <div className="dashboard-card">
                  <div className="dashboard-head">
                    <h3>District Overview</h3>
                    <span className="year-select">{previewSourceLabel}</span>
                  </div>
                  <div className="metric-grid">
                    <div className="metric-card">
                      <small>Total Schools</small>
                      <strong>{previewSchools ?? "—"}</strong>
                      <span className="metric-trend">{liveKpis ? "From state overview" : "Connect backend to load"}</span>
                    </div>
                    <div className="metric-card">
                      <small>Enrolment</small>
                      <strong>{previewStudents ?? "—"}</strong>
                      <span className="metric-trend">{liveKpis ? "Students enrolled" : "—"}</span>
                    </div>
                    <div className="metric-card">
                      <small>Data Entry</small>
                      <strong>{previewDataEntry ?? "—"}</strong>
                      <span className="metric-trend">{liveKpis ? "Completion rate" : "—"}</span>
                    </div>
                    <div className="metric-card">
                      <small>Avg OSH</small>
                      <strong>{previewOsh ?? "—"}</strong>
                      <span className="metric-trend">{liveKpis ? "School health index" : "—"}</span>
                    </div>
                  </div>

                  <div className="chart-grid">
                    <div className="chart-card">
                      <p className="chart-title">SQAAF Quality Bands</p>
                      {qualityDonut ? (
                        <div className="donut-wrap">
                          <div
                            className="donut"
                            aria-hidden="true"
                            style={donutStyle ? { background: donutStyle } : undefined}
                          />
                          <div className="legend">
                            {qualityDonut.map((seg) => (
                              <div key={seg.label} className="legend-row">
                                <span><i className="dot" />{seg.label}</span>
                                <b>{seg.pct}%</b>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <p className="chart-empty">SQAAF data unavailable</p>
                      )}
                    </div>
                    <div className="chart-card">
                      <p className="chart-title">Blocks by OSH Score</p>
                      {oshHistogram ? (
                        <div className="bar-chart" aria-hidden="true">
                          {oshHistogram.map((bin) => (
                            <div key={bin.label} className="bar-item">
                              <div className="bar" style={{ height: bin.heightPct }} />
                              {bin.label}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="chart-empty">Block OSH data unavailable</p>
                      )}
                    </div>
                  </div>

                  <div className="map-card">
                    <p className="chart-title">Block-wise Overall School Health Map</p>
                    <div className="map-bg" />
                    <div className="floating-insight">
                      <span className="icon-box" style={{ width: 54, height: 54, margin: 0 }}>
                        <svg className="large-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 16V9" /><path d="M12 16V5" /><path d="M17 16v-3" /></svg>
                      </span>
                      <strong>One Platform.<br />Every Insight.<br />Better Tomorrow.</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <LandingDistrictMapSection />

          <section className="section" id="dashboards">
            <div className="container">
              <div className="section-title">
                <span className="section-icon">
                  <svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="4" /><path d="M12 2v4M12 18v4M2 12h4M18 12h4" /></svg>
                </span>
                <h2>What You Can Do</h2>
                <p>Ten mission-ready dashboards in one place — designed for clarity, built for action, and aligned with school improvement outcomes.</p>
              </div>

              <div className="dashboard-list">
                {features.map((feature) => {
                  const isLoading = loadingCard === feature.title;

                  return (
                    <article
                      key={feature.title}
                      className={`feature-card${isLoading ? " loading" : ""}`}
                      onClick={() => handleFeatureClick(feature)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleFeatureClick(feature);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="icon-box"><FeatureIcon name={feature.icon} /></div>
                      <h3>{toTitleCase(feature.title)}</h3>
                      <p>{feature.description}</p>
                      <span className="card-arrow">→</span>
                      {isLoading && <div className="feature-loading">Signing in…</div>}
                    </article>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="container" id="impact">
            <div className="impact-panel">
              <h2>Driving Impact with Data</h2>
              <div className="impact-grid">
                <div className="impact-stat">
                  <span className="icon-box" style={{ margin: 0 }}><svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" /></svg></span>
                  <div><strong>{previewSchools ?? "—"}</strong><span className="impact-label">Total Schools</span></div>
                </div>
                <div className="impact-stat">
                  <span className="icon-box" style={{ margin: 0 }}><svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 21v-2a4 4 0 0 0-8 0v2" /><circle cx="12" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M2 21v-2a4 4 0 0 1 3-3.87" /></svg></span>
                  <div><strong>{previewStudents ?? "—"}</strong><span className="impact-label">Total Enrolment</span></div>
                </div>
                <div className="impact-stat">
                  <span className="icon-box" style={{ margin: 0 }}><svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></svg></span>
                  <div><strong>{previewDataEntry ?? "—"}</strong><span className="impact-label">Data Entry Rate</span></div>
                </div>
                <div className="impact-stat">
                  <span className="icon-box" style={{ margin: 0 }}><svg className="svg-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12Z" /><path d="M9 14.5 7 22l5-3 5 3-2-7.5" /></svg></span>
                  <div><strong>{previewOsh ?? "—"}</strong><span className="impact-label">Avg OSH Score</span></div>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default LandingPage;
